import { Localized, useLocalization } from "@fluent/react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { DataGrid, GridColDef, GridFilterModel, GridRowSelectionModel } from "@mui/x-data-grid";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { useMemo, useState } from "react";
import { useAppSelector } from "../../app/hooks";
import { AppId } from "../../app/appTypes";
import { LoadURSTemplates } from "../../app/AppDataLoader";
import { selectAllUrsTemplates, UrsTemplate } from "../urstemplates/ursTemplatesSlice";
import { StructureName } from "../device/Structure";
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar";
import { CancelButton } from "../../app/components/CancelButton";
import { selectSettings } from "../settings/settingsSlice";

export type UrsRequirementPackage = UrsTemplate;

type UrsRequirementPackagesSelectProps = {
    value: AppId | "";
    language?: string | null;
    structureId?: AppId | "" | null;
    onChange: (template: UrsRequirementPackage) => void;
    onClear?: () => void;
    error?: boolean;
    required?: boolean;
    showFilters?: boolean;
};

const UrsRequirementPackagesSelectInner = ({ value, language, structureId, onChange, onClear, error, showFilters = false }: UrsRequirementPackagesSelectProps) => {
    const { l10n } = useLocalization();
    const { locale } = useAppSelector(selectSettings);
    const [open, setOpen] = useState(false);
    const [selection, setSelection] = useState<GridRowSelectionModel>(value ? [String(value)] : []);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [], quickFilterValues: [] });
    const [languageCompatibleOnly, setLanguageCompatibleOnly] = useState(false);
    const [structureCompatibleOnly, setStructureCompatibleOnly] = useState(false);
    const allPackages = useAppSelector(selectAllUrsTemplates);

    const quickFilterTerms = useMemo(
        () => (filterModel.quickFilterValues ?? [])
            .map((v) => String(v).trim())
            .filter(Boolean),
        [filterModel.quickFilterValues]
    );

    const renderHighlightedText = (value: unknown) => {
        const text = String(value ?? "");

        if (!text || quickFilterTerms.length === 0) {
            return <>{text}</>;
        }

        const escapedTerms = quickFilterTerms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
        const regex = new RegExp(`(${escapedTerms.join("|")})`, "ig");
        const parts = text.split(regex);

        return (
            <>
                {parts.map((part, idx) => {
                    const isMatch = quickFilterTerms.some((term) => term.toLowerCase() === part.toLowerCase());
                    return isMatch ? (
                        <Box
                            key={`${part}-${idx}`}
                            component="mark"
                        >
                            <mark><b> {part}</b></mark>
                        </Box>
                    ) : (
                        <span key={`${part}-${idx}`}>{part}</span>
                    );
                })}
            </>
        );
    };

    const selectedPackage = useMemo(
        () => (value ? allPackages.find((p) => String(p.id) === String(value)) : undefined),
        [allPackages, value]
    );

    const visiblePackages = useMemo(() => {
        let rows = allPackages;

        if (languageCompatibleOnly && language) {
            const normalizedLanguage = language.trim().toLowerCase();
            rows = rows.filter((pkg) => String(pkg.language ?? "").trim().toLowerCase() === normalizedLanguage);
        }

        const hasStructureId = structureId !== undefined && structureId !== null && String(structureId).trim() !== "";
        if (structureCompatibleOnly && hasStructureId) {
            rows = rows.filter((pkg) => String(pkg.deviceTypeId) === String(structureId));
        }

        return rows;
    }, [allPackages, language, languageCompatibleOnly, structureCompatibleOnly, structureId]);

    const localeText = useMemo(() => {
        const en = enUS.components.MuiDataGrid.defaultProps.localeText;
        return locale === "pl"
            ? plPL.components.MuiDataGrid.defaultProps.localeText
            : en;
    }, [locale]);

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: "ursNo",
                headerName: l10n.getString("number", null, "_No"),
                width: 160,
                renderCell: (params) => renderHighlightedText(params.value),
            },
            {
                field: "title",
                headerName: l10n.getString("title", null, "_title"),
                flex: 1,
                minWidth: 220,
                renderCell: (params) => renderHighlightedText(params.value),
            },
            {
                field: "docDesignation",
                headerName: l10n.getString("description", null, "_Opis"),
                flex: 1,
                minWidth: 220,
                renderCell: (params) => renderHighlightedText(params.value),
            },
            {
                field: "deviceTypeId",
                headerName: l10n.getString("structure", null, "_structure"),
                minWidth: 220,
                renderCell: (params) => <StructureName structureId={params.row.deviceTypeId} />,
            },
            {
                field: "language",
                headerName: l10n.getString("language", null, "_language"),
            },
            {
                field: "isSystem",
                headerName: l10n.getString("system-item", null, "_systemItem"),
                type: "boolean",
                width: 120
            }
        ],
        [l10n, quickFilterTerms]
    );

    const confirmSelection = () => {
        const id = selection.length ? String(selection[0]) : "";
        if (id) {
            const pkg = allPackages.find((p) => String(p.id) === id);
            if (pkg) onChange(pkg);
        }
        setOpen(false);
    };

    const handleDialogClose = (_e: object, reason: "backdropClick" | "escapeKeyDown") => {
        if (reason === "backdropClick") return;
        setOpen(false);
    };

    const isSelected = !!value && !error;

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                {isSelected ? (
                    <CheckCircleIcon sx={{ color: "success.main", fontSize: 18 }} aria-hidden />
                ) : (
                    <PriorityHighIcon sx={{ color: "error.main", fontSize: 18 }} aria-hidden />
                )}
                <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: isSelected ? "text.secondary" : "error.main" }}
                >
                    {isSelected ? (
                        <Localized id="urs-requirements-package-selected">
                            <span>_Wybrana paczka</span>
                        </Localized>
                    ) : (
                        <Localized id="urs-requirements-package">
                            <span>_Paczka wymagań URS</span>
                        </Localized>
                    )}
                </Typography>
            </Stack>

            <Paper
                variant="outlined"
                onClick={() => setOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpen(true);
                    }
                }}
                sx={(theme) => ({
                    p: 1.25,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    borderColor: error ? theme.palette.error.main : theme.palette.divider,
                    "&:hover": { borderColor: theme.palette.text.primary },
                    "&:focus-visible": {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                    },
                })}
            >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                            <Localized id="urs-template-code">
                                <span>_Kod</span>
                            </Localized>: {selectedPackage?.ursNo ?? "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <Localized id="urs-template-description">
                                <span>_Opis</span>
                            </Localized>: {selectedPackage?.docDesignation ?? "—"}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {value && onClear && (
                            <Tooltip title={l10n.getString("clear-selected", null, "_Wyczyść wybór")}>
                                <IconButton
                                    size="small"
                                    aria-label={l10n.getString("clear-selected", null, "_Wyczyść wybór")}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onClear();
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={l10n.getString("open-template-list", null, "_Otwórz listę szablonów")}>
                            <IconButton
                                size="small"
                                aria-label={l10n.getString("open-template-list", null, "_Otwórz listę szablonów")}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpen(true);
                                }}
                            >
                                <SearchIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Paper>

            <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Localized id="urs-requirements-package">
                        <span>_Paczka wymagań URS</span>
                    </Localized>
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={1.5}>
                        {showFilters && (
                        <Paper
                            variant="outlined"
                            sx={{
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 1.5,
                                backgroundColor: (theme) => theme.palette.grey[50],
                            }}
                        >
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                spacing={0.75}
                                alignItems={{ xs: "flex-start", md: "center" }}
                                justifyContent="space-between"
                            >
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                        {l10n.getString("filters", null, "Filters")}
                                    </Typography>
                                </Box>

                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1.5}
                                    useFlexGap
                                    flexWrap="wrap"
                                    sx={{ columnGap: 2, rowGap: 0.5 }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                size="small"
                                                checked={languageCompatibleOnly}
                                                onChange={(_, checked) => setLanguageCompatibleOnly(checked)}
                                                color="primary"
                                            />
                                        }
                                        label={l10n.getString("urs-filter-language-compatible", null, "Only matching language")}
                                        sx={{ mr: 0, my: 0 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                size="small"
                                                checked={structureCompatibleOnly}
                                                onChange={(_, checked) => setStructureCompatibleOnly(checked)}
                                                color="primary"
                                            />
                                        }
                                        label={l10n.getString("urs-filter-structure-compatible", null, "Only matching structure")}
                                        sx={{ mr: 0, my: 0 }}
                                    />
                                </Stack>
                            </Stack>
                        </Paper>
                        )}

                        <Box sx={{ height: "60vh", minHeight: 300, width: "100%" }}>
                        <DataGrid
                            rows={visiblePackages}
                            columns={columns}
                            localeText={localeText}
                            density="compact"
                            filterModel={filterModel}
                            onFilterModelChange={setFilterModel}
                            rowSelectionModel={selection}
                            onRowSelectionModelChange={(model) => {
                                const id = model.length ? String(model[0]) : "";
                                setSelection(id ? [id] : []);
                            }}
                            slots={{ toolbar: QuickSearchToolbar }}
                            initialState={{
                                sorting: { sortModel: [{ field: "ursNo", sort: "asc" }] },
                            }}
                        />
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button
                        variant="contained"
                        size="small"
                        color="success"
                        onClick={confirmSelection}
                        disabled={!selection.length}
                    >
                        <Localized id="select">
                            <span>_Wybierz</span>
                        </Localized>
                    </Button>

                    <CancelButton loading={false} onClick={() => setOpen(false)} />
                </DialogActions>
            </Dialog>
        </>
    );
};

export const UrsRequirementPackagesSelect = (props: UrsRequirementPackagesSelectProps) => (
    <LoadURSTemplates component={<Skeleton variant="rectangular" height={56} />}>
        <UrsRequirementPackagesSelectInner {...props} />
    </LoadURSTemplates>
);
